// src/pages/CallbackPage.js
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // named import as your package exports
import { api } from '../utils/api'; // adjust path if needed
import { showErrorToast } from '../components/Toast';

const CallbackPage = () => {
  const navigate = useNavigate();
  const hasHandledCallback = useRef(false);

  useEffect(() => {
    if (hasHandledCallback.current) return;
    hasHandledCallback.current = true;

    // Ends a failed sign-in: tells the user why, and (unless the caller already restored a
    // previous session, as the identity-linking flow does) drops the rejected tokens so the
    // app doesn't treat the user as logged in.
    const failLogin = (message, { keepSession = false } = {}) => {
      if (!keepSession) {
        ["access_token", "id_token", "user_info", "pending_verification"].forEach((k) => localStorage.removeItem(k));
      }
      // Any failed callback ends an in-flight "connect account" attempt. Left behind, these flags turn
      // the user's next normal sign-in into a linking request against the old session token.
      ["linkaccount", "linkaccount_time", "access_id"].forEach((k) => localStorage.removeItem(k));
      if (message) showErrorToast(message);
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate("/login", { replace: true });
    };

    const hash = window.location.hash || "";
    const raw = hash.replace(/^#/, "");
    const params = new URLSearchParams(raw);

    const err = params.get("error");
    const errDesc = params.get("error_description");
    if (err) {
      console.error("Auth callback error:", err, errDesc);
      // Auth0 returns access_denied when the user cancels or declines permissions on the provider page
      failLogin(
        err === "access_denied"
          ? "Sign-in was cancelled or permission was not granted. Please try again."
          : errDesc || "Sign-in failed. Please try again or use another login method.",
        { keepSession: true }
      );
      return;
    }

    const accessToken = params.get("access_token");
    const idToken = params.get("id_token");
    const stateParam = params.get("state");

    if (!accessToken && !idToken) {
      console.error("No tokens found in auth callback.");
      failLogin("Sign-in did not complete. Please try again.", { keepSession: true });
      return;
    }

    // parse optional returnTo from state
    let returnTo = "/dashboard";
    if (stateParam) {
      try {
        const parsed = JSON.parse(stateParam);
        if (parsed?.returnTo) returnTo = parsed.returnTo;
      } catch (e) {
        // ignore
      }
    }

    (async () => {
      try {
        // Check if this is an identity linking flow BEFORE storing new tokens
        const linkAccount = localStorage.getItem('linkaccount');
        
        // Save original session data before overwriting (needed for rollback on failure)
        const originalAccessToken = localStorage.getItem("access_token");
        const originalIdToken = localStorage.getItem("id_token");
        const originalUserInfo = localStorage.getItem("user_info");
        
        // Store new tokens (but keep original for rollback)
        if (accessToken) localStorage.setItem("access_token", accessToken);
        if (idToken) localStorage.setItem("id_token", idToken);

        if (idToken) {
          try {
            const user = jwtDecode(idToken);
            localStorage.setItem("user_info", JSON.stringify(user));
          } catch (e) {
            console.warn("ID token decode failed:", e);
          }
        }

        // Immediately call protected API to confirm user status
        const tokenToUse = accessToken || localStorage.getItem("access_token");
        if (!tokenToUse) {
          // no token -> fallback to login
          failLogin("Sign-in did not complete. Please try again.", { keepSession: linkAccount === 'true' });
          return;
        }

        try {
          console.log('Link account flag:', linkAccount);
          if (linkAccount === 'true') {
            const accessId = localStorage.getItem('access_id') || originalAccessToken;
            const userInfoRaw = localStorage.getItem('user_info');
            let userInfo = null;
            try {
              userInfo = userInfoRaw ? JSON.parse(userInfoRaw) : null;
            } catch (e) {
              userInfo = null;
            }
            if (userInfo && accessId) {
              const [provider] = userInfo.sub.split('|');
              const payload = {
                provider: provider === 'auth0' ? 'auth0' : provider,
                userId: userInfo.sub,
                connection: provider === 'auth0' ? 'Username-Password-Authentication' : provider,
                identifier: userInfo.email || userInfo.phone_number || userInfo.identifier,
                isSocial: userInfo.isSocial
              };
              const res = await api.post('/users/add-identity', payload, {
                headers: {
                  Authorization: `Bearer ${accessId}`,
                },
              });
              if (res.status === 200) {
                localStorage.setItem('access_token', accessId);
                if (originalIdToken) {
                  localStorage.setItem('id_token', originalIdToken);
                } else {
                  localStorage.removeItem('id_token');
                }
                if (originalUserInfo) {
                  localStorage.setItem('user_info', originalUserInfo);
                } else {
                  localStorage.removeItem('user_info');
                }
                localStorage.removeItem('linkaccount');
                localStorage.removeItem('access_id');
                
                // Store success flag for phone verification
                localStorage.setItem('phone_verified_success', 'true');
                localStorage.setItem('phone_verified_success_time', Date.now().toString());
                
                // Get the phone number that was linked and update user details
                // Use phone_number from userInfo, or fallback to identifier if it looks like a phone
                let linkedPhoneNumber = userInfo?.phone_number;
                if (!linkedPhoneNumber && userInfo?.identifier) {
                  // Check if identifier looks like a phone number (starts with +)
                  if (userInfo.identifier.startsWith('+')) {
                    linkedPhoneNumber = userInfo.identifier;
                  }
                }
                
                
                if (linkedPhoneNumber) {
                  try {
                    // First get the user to get the internal user ID
                    const userResponse = await api.get('/users/me', {
                      headers: { Authorization: `Bearer ${accessId}` },
                    });
                    
                    
                    // Get user ID from response - check various possible locations
                    const userData = userResponse?.data;
                    const userId = userData?.user?.id || userData?.id || userData?._id;
                    
                    
                    if (userId) {
                      // Use PATCH /users/{userId} with phone field
                      await api.patch(`/users/${userId}`, { phone: linkedPhoneNumber }, {
                        headers: { Authorization: `Bearer ${accessId}` },
                      });
                      console.log('User phone number updated successfully');
                    } else {
                      console.warn('Could not find user ID in response');
                    }
                  } catch (updateErr) {
                    console.warn('Failed to update user phone number:', updateErr);
                  }
                }
              }
            }
          } else {
            const resp = await api.get("/users/me", {
              headers: { Authorization: `Bearer ${tokenToUse}` },
            });
            const userData = resp.data;
            const isFirstLogin = localStorage.getItem('currencySetDone') !== userData.id;
            const storedCurrency = localStorage.getItem('user_currency');
            const storedCountry = localStorage.getItem('user_country');
            if (isFirstLogin && storedCurrency && userData.id) {
              localStorage.setItem('detectedCurrency', storedCurrency);
              localStorage.setItem('currencySetDone', userData.id);
              try {
                await api.patch(`/users/${userData.id}`, {
                  currency: storedCurrency,
                  region: storedCurrency,
                  address: { ...userData.address, country: storedCountry || userData.address?.country }
                }, { headers: { Authorization: `Bearer ${tokenToUse}` } });
              } catch (e) {
                console.warn('Failed to update currency on first OAuth login:', e);
              }
            }
          }
          // success -> everything ok, navigate to returnTo (dashboard)
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate(returnTo, { replace: true });
        } catch (apiErr) {
          const status = apiErr?.response?.status;
          const message = (apiErr?.response?.data?.message || apiErr?.message || "").toString().toLowerCase();

          // Handle identity linking failure - restore original session and clean up
          if (linkAccount === 'true') {
            // Always clean up identity linking flags
            localStorage.removeItem('linkaccount');
            localStorage.removeItem('access_id');
            
            // Restore original session data (rollback the token overwrite)
            if (originalAccessToken) {
              localStorage.setItem("access_token", originalAccessToken);
            } else {
              localStorage.removeItem("access_token");
            }
            if (originalIdToken) {
              localStorage.setItem("id_token", originalIdToken);
            } else {
              localStorage.removeItem("id_token");
            }
            if (originalUserInfo) {
              localStorage.setItem("user_info", originalUserInfo);
            } else {
              localStorage.removeItem("user_info");
            }
          }

          // if backend signals email verification required -> set pending flag and go to login page
          const needsVerification = status === 403 && message.includes("email");
          if (needsVerification) {
            const storedUser = JSON.parse(localStorage.getItem("user_info") || "{}");

            // Social accounts that didn't share an email (e.g. a Facebook account registered with a
            // phone number) can never pass email verification, so don't send them to the verify screen
            if (!storedUser?.email && linkAccount !== 'true') {
              failLogin(
                "Your account didn't share an email address with myworth.ai. Please sign up with email or phone, then connect this account from My Profile."
              );
              return;
            }

            // store a short pending flag so LoginPage shows verify UI
            localStorage.setItem(
              "pending_verification",
              JSON.stringify({
                email: storedUser?.email || null,
                msg: apiErr?.response?.data?.message || "Please verify your email.",
                ts: Date.now(),
              })
            );

            window.history.replaceState({}, document.title, window.location.pathname);
            // navigate to login where in-place verification UI will show
            navigate("/login", { replace: true });
            return;
          }

          // Handle identity conflict errors gracefully - close modal and show error without redirect
          const isIdentityConflict = 
            status === 400 || 
            status === 409 ||
            message.includes("identity") || 
            message.includes("already in use") ||
            message.includes("same identity");
            
          if (linkAccount === 'true' && isIdentityConflict) {
            console.error("Identity linking conflict:", apiErr);
            // Store the error message in localStorage for Dashboard to read
            localStorage.setItem('phone_link_error', apiErr?.response?.data?.message || "This identity is already linked to another account");
            localStorage.setItem('phone_link_error_time', Date.now().toString());
            // Show error toast
            showErrorToast(apiErr?.response?.data?.message || "This identity is already linked to another account");
            // Clean up and stay on current page (don't redirect)
            window.history.replaceState({}, document.title, window.location.pathname);
            // Navigate back to where they came from (dashboard) without adding to history
            navigate(returnTo, { replace: true });
            return;
          }

          // otherwise fallback to login showing error
          console.error("API /users/me failed after callback:", apiErr);
          failLogin(
            apiErr?.response?.data?.message || apiErr?.message || "API request failed",
            { keepSession: linkAccount === 'true' } // linking flow already restored the original session above
          );
        }
      } catch (e) {
        console.error("Error processing callback:", e);
        failLogin("Something went wrong while signing you in. Please try again.", {
          keepSession: localStorage.getItem('linkaccount') === 'true',
        });
      }
    })();
  }, [navigate]);

  return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',background:'#fff'}}>
      <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem', marginBottom: 16}}>
          <span className="visually-hidden">Loading...</span>
        </div>
        {/* <div style={{marginTop:8}}>Finalizing sign in...</div> */}
      </div>
    </div>
  );
};

export default CallbackPage;
